import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, Grid, TextField } from '@mui/material';
import { useAppSelector } from 'app/hooks';
import AnalyzerSelectLocal from 'features/analyzers/AnalyzerSelectLocal';
import SnapshotSelectLocal from 'features/snapshots/SnapshotSelectLocal';
import { Control, Controller } from 'react-hook-form';
import { useState } from 'react';
import ShareLinkDialog from 'features/traceHistory/ShareLinkDialog';
import { WeatherConstraint } from './types';
import { makeShareWeatherUrl } from './utils/urlManagers';
import {
  convertFromImperial,
  convertNumberToSaveToDB,
} from 'features/weather/utils/dataConverters';
import { MeasurementData } from 'features/weather/types';

import { useSnapshots } from 'features/snapshots/api/getSnapshots';
import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';

interface Props {
  control: Control<
    { fromDate: Date; toDate: Date; analyzerId: number; snapshotId: number },
    object
  >;
  watch: (names?: string | string[]) => number;
  handleSubmit: () => void;
  weatherConstraints: WeatherConstraint[];
  traceIndex: number;
  readyToShare: boolean;
}

const WeatherReflectionFilters = ({
  control,
  watch,
  handleSubmit,
  weatherConstraints,
  traceIndex,
  readyToShare,
}: Props) => {
  const { data: analyzers } = useAnalyzers({});
  const { data: snapshots } = useSnapshots({ analyzerId: watch('analyzerId') });
  const { dateFormatWithZeros, timeFormat, ampm, timezone } = useUserDateTime();
  const inImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const handleShareHistory = () => {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol;
      const host = window.location.host;
      const urlString = makeShareWeatherUrl(
        protocol,
        host,
        new Date(watch('fromDate')),
        new Date(watch('toDate')),
        watch('analyzerId'),
        watch('snapshotId'),
        traceIndex,
        // @ts-ignore
        weatherConstraints.map((constraint) => ({
          ...constraint,
          level: convertFromImperial(
            convertNumberToSaveToDB(
              Number(constraint.level),
              constraint.measurement as MeasurementData
            ),
            constraint.measurement as MeasurementData,
            inImperial
          )?.toFixed(0),
        })),
        timezone
      );
      setLinkUrl(urlString);
      setLinkDialogOpen(true);
    }
  };
  return (
    <Grid
      item
      container
      justifyContent='space-between'
      style={{ paddingRight: '4rem' }}
    >
      <Grid item xs container spacing={1}>
        <Grid item style={{ width: '250px' }}>
          <Controller
            name='fromDate'
            control={control}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label='From'
                  inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                  value={value}
                  onChange={onChange}
                  renderInput={(props) => <TextField {...props} />}
                  ampm={ampm}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ width: '250px' }}>
          <Controller
            name='toDate'
            control={control}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label='To'
                  inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                  value={value}
                  onChange={onChange}
                  renderInput={(props) => <TextField {...props} />}
                  ampm={ampm}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ width: '150px' }}>
          <Controller
            name='analyzerId'
            control={control}
            rules={{ required: true, validate: (value) => value !== -1 }}
            render={({ field: { onChange, value } }) => (
              <AnalyzerSelectLocal
                analyzers={analyzers}
                selectedAnalyzerId={value}
                handleAnalyzerChanged={(e) => onChange(e.target.value)}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item style={{ width: '150px' }}>
          <Controller
            name='snapshotId'
            control={control}
            rules={{ required: true, validate: (value) => value !== -1 }}
            render={({ field: { onChange, value } }) => (
              <SnapshotSelectLocal
                snapshots={snapshots}
                selectedSnapshotId={value}
                handleSnapshotChanged={onChange}
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item style={{ padding: '.5rem', paddingRight: 0 }}>
        <Button
          onClick={handleShareHistory}
          variant='contained'
          color='primary'
          fullWidth
          disabled={!readyToShare}
        >
          Share
        </Button>
      </Grid>
      <Grid item style={{ padding: '.5rem' }}>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={
            watch('snapshotId') === -1 ||
            watch('analyzerId') === -1 ||
            watch('fromDate') > watch('toDate')
          }
        >
          Get History
        </Button>
      </Grid>
      <ShareLinkDialog
        open={linkDialogOpen}
        setOpen={setLinkDialogOpen}
        link={linkUrl}
      />
    </Grid>
  );
};

export default WeatherReflectionFilters;
